import type { EditingState } from "@/common/client_data"
import { presetNames } from "@/signomatic/app/sign_validator"
import Sign from "@/signomatic/app/sign"
import { type Actions, ActionTypes } from "./actions"
import type { SignHistoryState } from "@/signomatic/app/sign_history"
import ClientData from "@/common/client_data"
import { defaultToolbox, autoOpenToolbox, setSignTypeVisited } from "@/signomatic/util/toolboxes"

export interface State {
  signType: string
  metaProduct: string
  toolbox: string | null
  nextToolbox: string | null
  presetGroup: string | null
  subToolboxStack: string[]
  subToolbox: string | null
  subToolboxTarget: string | null
  signs: SignType[]
  signState: SignState | null
  activeObject: SignObjectState | null
  signData: SignData[]
  currentSignData: SignData | null
  previewModal: PreviewModal
  editing: EditingState | null
  fileImportOpen: boolean
  fontUploadOpen: boolean
  history: SignHistoryState | null
  toolboxHeight: number | null
}

export const initialState: State = {
  signType: "",
  metaProduct: "",
  toolbox: null,
  nextToolbox: null,
  presetGroup: null,
  subToolboxStack: [],
  subToolbox: null,
  subToolboxTarget: null,
  signs: [],
  signData: [],
  signState: null,
  activeObject: null,
  currentSignData: null,
  previewModal: {},
  editing: null,
  fileImportOpen: false,
  fontUploadOpen: false,
  history: null,
  toolboxHeight: null,
}

export const reducer = (state: State, action: Actions): State => {
  switch (action.type) {
    case ActionTypes.SET_SIGN_TYPE: {
      const { payload: type } = action

      if (type === state.signType && !state.metaProduct) {
        return state
      }

      let toolbox = state.toolbox
      let subToolbox = state.subToolbox

      if (
        autoOpenToolbox(type) &&
        state.signType == "" &&
        state.metaProduct == "" &&
        !state.editing &&
        !ClientData.exampleID
      ) {
        toolbox = defaultToolbox(type, state.currentSignData)
      } else if (toolbox !== "products") {
        toolbox = null
        subToolbox = null
      }

      let stayEditing = state.signType === "" ? state.editing : null
      if (state.editing && state.editing.typeUpdate) {
        stayEditing = state.editing
      }

      setSignTypeVisited(type)

      return {
        ...state,
        toolbox,
        subToolbox,
        signType: type,
        metaProduct: "",
        editing: stayEditing,
      }
    }
    case ActionTypes.SET_META_SIGN_TYPE: {
      const { metaProduct, signType } = action.payload

      if (signType === state.signType && metaProduct == state.metaProduct) {
        return state
      }

      let toolbox = state.toolbox
      if (
        autoOpenToolbox(metaProduct) &&
        state.signType == "" &&
        state.metaProduct == "" &&
        !state.editing &&
        !ClientData.exampleID
      ) {
        toolbox = defaultToolbox(metaProduct, state.currentSignData)
      }
      const subToolbox = state.subToolbox

      let stayEditing = state.signType === "" ? state.editing : null
      if (state.editing && state.editing.typeUpdate) {
        stayEditing = state.editing
      }

      setSignTypeVisited(metaProduct)

      return {
        ...state,
        toolbox,
        subToolbox,
        signType: signType || metaProduct,
        metaProduct,
        editing: stayEditing,
      }
    }
    case ActionTypes.SET_TOOLBOX: {
      const { type, index } = action.payload

      if (index !== undefined && index > -1) {
        if (index === 0) {
          let toolbox = null
          let nextToolbox = state.nextToolbox

          if (state.nextToolbox && state.signState) {
            toolbox = state.nextToolbox
            nextToolbox = null

            if (toolbox === "presets" && state.currentSignData) {
              const names = presetNames(state.currentSignData)
              toolbox = `presets_${names[0]}`
            }
          }

          if (state.toolbox === "color" && state.subToolbox === "customcolor") {
            return {
              ...state,
              toolbox: "color",
              subToolbox: null,
              subToolboxStack: [],
            }
          }

          return {
            ...state,
            toolbox,
            nextToolbox,
            subToolbox: null,
            subToolboxStack: [],
            subToolboxTarget: null,
          }
        } else {
          const stack = [...state.subToolboxStack]

          if (state.subToolbox === "customcolor") {
            stack.pop()

            return {
              ...state,
              subToolbox: "color",
              subToolboxStack: stack,
            }
          }

          if (state.subToolbox) {
            stack.push(state.subToolbox)
          }

          stack.splice(index - 1)

          return {
            ...state,
            subToolbox: stack.pop() || null,
            subToolboxStack: stack,
          }
        }
      }

      if (!type) {
        if (state.subToolbox) {
          const subToolboxStack = [...state.subToolboxStack]
          const subToolbox = subToolboxStack.pop() || null
          return { ...state, subToolboxStack, subToolbox }
        } else {
          let toolbox = null
          let nextToolbox = state.nextToolbox

          if (state.nextToolbox && state.signState) {
            toolbox = state.nextToolbox
            nextToolbox = null

            if (toolbox === "presets" && state.currentSignData) {
              const names = presetNames(state.currentSignData)
              toolbox = `presets_${names[0]}`
            }
          }

          return { ...state, toolbox, subToolbox: null, subToolboxStack: [], nextToolbox }
        }
      }

      return {
        ...state,
        toolbox: type,
        subToolbox: null,
        subToolboxStack: [],
        subToolboxTarget: null,
      }
    }
    case ActionTypes.SET_STATE_CHANGE_TOOLBOX: {
      return { ...state, nextToolbox: action.payload }
    }
    case ActionTypes.ADD_SUB_TOOLBOX: {
      if (state.subToolbox === action.payload.type) {
        return {
          ...state,
          subToolboxTarget: action.payload.target || null,
        }
      }

      const subToolboxStack =
        action.payload.type && state.subToolbox ? [...state.subToolboxStack, state.subToolbox] : []

      return {
        ...state,
        subToolboxStack,
        subToolbox: action.payload.type,
        subToolboxTarget: action.payload.target || null,
      }
    }
    case ActionTypes.SET_SUB_TOOLBOX: {
      return {
        ...state,
        subToolboxStack: [],
        subToolbox: action.payload.type,
        subToolboxTarget: action.payload.target || null,
      }
    }
    case ActionTypes.SET_SUB_TARGET: {
      return {
        ...state,
        subToolboxTarget: action.payload.target || null,
      }
    }
    case ActionTypes.RESET_TOOLBOX: {
      return {
        ...state,
        toolbox: null,
        subToolboxStack: [],
        subToolbox: null,
        subToolboxTarget: null,
        nextToolbox: null,
      }
    }
    case ActionTypes.RESET_SIZE_TOOLBOX: {
      if (state.toolbox === "size") {
        return {
          ...state,
          toolbox: null,
          subToolboxStack: [],
          subToolbox: null,
          subToolboxTarget: null,
          nextToolbox: null,
        }
      } else {
        return state
      }
    }
    case ActionTypes.TOGGLE_TOOLBOX: {
      const toolbox = action.payload

      if (state.toolbox) {
        return {
          ...state,
          toolbox: null,
          subToolboxStack: [],
          subToolbox: null,
          subToolboxTarget: null,
          nextToolbox: null,
        }
      } else {
        return {
          ...state,
          toolbox,
          subToolbox: null,
          subToolboxStack: [],
          subToolboxTarget: null,
        }
      }
    }
    case ActionTypes.SET_TOOLBOX_HEIGHT: {
      return {
        ...state,
        toolboxHeight: action.payload,
      }
    }
    case ActionTypes.SET_SIGN_STATE: {
      const { signState, signData } = action.payload

      const currentData = signData.find(data => data.name === state.signType)

      let toolbox = state.toolbox
      let nextToolbox = state.nextToolbox

      if (state.nextToolbox && state.toolbox === null) {
        toolbox = state.nextToolbox
        nextToolbox = null

        if (toolbox === "presets" && currentData) {
          const names = presetNames(currentData)
          toolbox = `presets_${names[0]}`
        }
      }

      return {
        ...state,
        toolbox,
        nextToolbox,
        signState: { ...signState },
        signData,
        currentSignData: currentData || null,
      }
    }
    case ActionTypes.SET_ACTIVE_OBJECT: {
      if (action.payload) {
        let toolbox = state.toolbox

        if (toolbox === "image" && Sign.isText(action.payload)) {
          toolbox = "text"
        }

        if (toolbox === "text" && Sign.isImage(action.payload)) {
          toolbox = "image"
        }

        return { ...state, activeObject: action.payload, toolbox }
      }

      if (state.subToolbox && ["color", "customcolor", "border", "objectborder"].includes(state.subToolbox)) {
        return {
          ...state,
          activeObject: null,
          subToolboxStack: [],
          subToolbox: null,
          subToolboxTarget: null,
        }
      }

      return { ...state, activeObject: null }
    }
    case ActionTypes.OPEN_FILE_IMPORT: {
      return { ...state, fileImportOpen: true }
    }
    case ActionTypes.CLOSE_FILE_IMPORT: {
      return { ...state, fileImportOpen: false }
    }
    case ActionTypes.SET_HISTORY: {
      if (action.payload) {
        return { ...state, history: action.payload }
      }

      return state
    }
    case ActionTypes.OPEN_FONT_UPLOAD: {
      return { ...state, fontUploadOpen: true }
    }
    case ActionTypes.CLOSE_FONT_UPLOAD: {
      return { ...state, fontUploadOpen: false }
    }
    case ActionTypes.REMOVE_EDITING: {
      return { ...state, editing: null }
    }
    default: {
      return state
    }
  }
}
